const MOONROCK_API = process.env.REACT_APP_MOONROCK_API_URL || 'https://moonrock-9ocod.ondigitalocean.app';
const MOONROCK_KEY = process.env.REACT_APP_MOONROCK_ANALYTICS_KEY || '';

export const moonrockUrl = (path: string, params: Record<string, string | number | boolean | undefined | null> = {}) => {
  const search = new URLSearchParams();
  search.set('key', MOONROCK_KEY);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') search.set(k, String(v));
  });
  return `${MOONROCK_API}${path}?${search.toString()}`;
};

export const fetchMoonrock = async <T>(path: string, params?: Record<string, any>, signal?: AbortSignal): Promise<T> => {
  const res = await fetch(moonrockUrl(path, params), { signal });
  if (!res.ok) throw new Error(`Moonrock API error: ${res.status}`);
  return res.json();
};

export const postMoonrock = async <T>(path: string, body: Record<string, any>): Promise<T> => {
  const res = await fetch(moonrockUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Moonrock API error: ${res.status}`);
  }
  return res.json();
};

// The Moonrock server's analytics responses use different field names than the dashboard page
// (e.g. retention as a list, funnel as { step, users }). These adapters accept either shape.
const toPercent = (rate: any) => {
  const n = Number(rate);
  if (!isFinite(n)) return 0;
  return n <= 1 ? n * 100 : n;
};

export const normalizeSummary = (raw: any) => ({
  totalUsers: raw?.totalUsers ?? 0,
  dau: raw?.dau ?? 0,
  wau: raw?.wau ?? 0,
  mau: raw?.mau ?? 0,
  eventsToday: raw?.eventsToday ?? raw?.totalEventsToday ?? 0,
  topEvents: raw?.topEvents ?? raw?.topEventsToday ?? [],
  signupsToday: raw?.signupsToday ?? 0,
});

export const normalizeRetention = (raw: any) => {
  if (raw && Array.isArray(raw.retention)) {
    const rateFor = (day: number) => {
      const row = raw.retention.find((item: any) => Number(item.day) === day);
      return row ? toPercent(row.rate) : 0;
    };
    return { day1: rateFor(1), day7: rateFor(7), day30: rateFor(30) };
  }
  return { day1: raw?.day1 ?? 0, day7: raw?.day7 ?? 0, day30: raw?.day30 ?? 0 };
};

export const normalizeFunnel = (raw: any) => {
  const list: any[] = Array.isArray(raw?.steps) ? raw.steps : Array.isArray(raw?.funnel) ? raw.funnel : [];
  return {
    steps: list.map((item) => ({
      name: String(item.name ?? item.step ?? ''),
      count: Number(item.count ?? item.users ?? 0),
      rate: toPercent(item.rate),
    })),
  };
};

export const normalizeEvents = (raw: any) => ({
  events: Array.isArray(raw?.events) ? raw.events : [],
  total: raw?.total ?? raw?.pagination?.total ?? 0,
  page: raw?.page ?? raw?.pagination?.page ?? 1,
  pages: raw?.pages ?? raw?.pagination?.pages ?? 0,
});

export const normalizeList = (raw: any, key: string) => ({ [key]: Array.isArray(raw?.[key]) ? raw[key] : [] });
